import {BehaviorsRequest, BehaviorsResultsMap} from 'app_api/projects/behaviors/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {MetricType} from 'common/query-builder/query-block/constants';
import {MetricEntry, MetricEntryBehavior} from 'common/query-builder/query-entry-section/types';
import {FeatureGate} from 'common/report/constants';
import {transformMetricEntryBehaviorToMetricBehavior} from 'common/report/insights/util/events';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {getProjectId} from 'common/report/util';
import {buildCreateEntityModalAttrs} from 'common/report/util/saved-entities';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {promptSharing} from 'common/widgets/sharing';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {capitalize} from 'common/util/string';

type UpsertResults = {
  isCreating: boolean;
  results: BehaviorsResultsMap;
};

export type EditableState = {
  showEditControls: true;
  canEdit: boolean;
  canSave: boolean;
  isSaved: boolean;
  isSaving: boolean;
  saveDisabledMessage: string;
};

export type EditState =
  | EditableState
  | {
      showEditControls: false;
      saveDisabledMessage: string;
    };

export function getTrackingProps(entry: MetricEntry) {
  return {
    id: entry.behavior.id,
    type: entry.behavior.type,
    'is expanded': entry.isExpanded,
    'is hidden': entry.isHidden,
    'type of metrics': Array.from(new Set(entry.behavior.behaviors?.map((behavior) => behavior.type))),
  };
}

export function getAllSavedBehaviors({behaviors, metricType}: {behaviors: BehaviorsResource; metricType: MetricType}) {
  return (
    behaviors
      .getAllValues()
      // fresh deleted behaviors result in `null` in lexicon
      .filter(Boolean)
      .filter((metric) => metric.type === metricType && metric.can_view)
  );
}

export async function saveBehavior({
  behavior,
  currentProject,
  behaviors,
  saveAsNew = false,
  previouslyExisting = false,
}: {
  behavior: MetricEntryBehavior;
  currentProject: CurrentProjectResource;
  behaviors: BehaviorsResource;
  saveAsNew: boolean;
  previouslyExisting: boolean;
}) {
  const {isCreating, results} = saveAsNew
    ? await createNewBehavior({behavior, currentProject, behaviors, previouslyExisting})
    : await updateExistingBehavior({behavior, behaviors});

  // created or updated
  if (results) {
    behaviors.update(results, {partialUpdate: false});
  }

  return {isCreating, results};
}

async function createNewBehavior({
  behavior,
  currentProject,
  behaviors,
  previouslyExisting,
}: {
  behavior: MetricEntryBehavior;
  currentProject: CurrentProjectResource;
  behaviors: BehaviorsResource;
  previouslyExisting: boolean;
}): Promise<UpsertResults> {
  const type = behavior.type === MetricType.Funnel ? `funnel` : `retention`;

  const {id, name: rawName, ...rawBehavior} = behavior;
  const bookmarkBehavior = transformMetricEntryBehaviorToMetricBehavior(rawBehavior);
  const {name: savedName, filters: _filters, ...behaviorDefinition} = bookmarkBehavior;
  const name = savedName || rawName;
  const newName = previouslyExisting ? `${name} (copy)` : `${name}`;
  const behaviorEntity: BehaviorsRequest = {
    type,
    name: newName,
    description: ``,
    definition: {
      behavior: behaviorDefinition,
    },
  };

  const result = await createEntityModalPromise(
    buildCreateEntityModalAttrs({
      projectId: parseInt(getProjectId(), 10),
      entityType: EntityType.Behavior,
      modalOpen: true,
      isEditing: false,
      isDuplicating: false,
      entity: behaviorEntity,
      showShareManagement: currentProject.get()?.value?.getFeatureGate(FeatureGate.Sharing),
      modalTitleOverride: `Save ${capitalize(type)} Behavior`,
    }),
    {
      existingEntityNames: behaviors.getAllValues().map((value) => value.name),
    },
  );

  if (result.didSave) {
    let results: BehaviorsResultsMap;
    try {
      results = await behaviors.create({
        ...behaviorEntity,
        name: result.name,
        description: result.description,
      });
    } catch {
      results = null;
      addToast({
        message: `Sorry, something went wrong. Please contact support if this problem persists.`,
        type: ToastType.ERROR,
        timeout: null,
      });
    }

    if (result.didShare) {
      await promptSharing({
        type: SharableEntityType.Behavior,
        id: Number(Object.keys(results)[0]),
        'project-id': Number(getProjectId()),
      });
    }
    return {
      isCreating: true,
      results,
    };
  }
  return {
    isCreating: false,
    results: null,
  };
}

async function updateExistingBehavior({
  behavior,
  behaviors,
}: {
  behavior: MetricEntryBehavior;
  behaviors: BehaviorsResource;
}): Promise<UpsertResults> {
  const type = behavior.type === MetricType.Funnel ? `funnel` : `retention`;
  let results: BehaviorsResultsMap;
  const {id, ...rawBehavior} = behavior;
  const bookmarkBehavior = transformMetricEntryBehaviorToMetricBehavior(rawBehavior);
  const {name, filters: _filters, ...behaviorDefinition} = bookmarkBehavior;
  try {
    results = await behaviors.patch(id, {
      type,
      id,
      name: behavior.name,
      definition: {
        behavior: behaviorDefinition,
      },
    });
  } catch {
    results = null;
    addToast({
      message: `Sorry, something went wrong. Please contact support if this problem persists.`,
      type: ToastType.ERROR,
      timeout: null,
    });
  }

  if (results) {
    return {
      isCreating: false,
      results,
    };
  }
  return {
    isCreating: false,
    results: null,
  };
}

// Simple validation for the current codebase
export function hasValidBehavior(behavior: MetricEntryBehavior) {
  const behaviors = behavior.behaviors || [];
  const hasValidBehaviors = behaviors.every((behavior) => hasValidBehavior(behavior));
  if (behavior.type === MetricType.Funnel) {
    return behaviors.length >= 2 && hasValidBehaviors;
  }
  if (behavior.type === MetricType.Retention) {
    return behaviors.length === 2 && hasValidBehaviors;
  }
  const filters = behavior.filters || [];
  return !!behavior.name && filters.every((filter) => new PropertyFilter(filter).isValid());
}
